<template>
  <div class="overflow-auto">
    <item
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :current="current"
        @conversation-select="onSelect"
    ></item>
    <infinite-loading @infinite="loadConversations" spinner="waveDots" :distance="1600"
                      :identifier="infiniteLoadKey">
      <template slot="no-more">
        <h5><span class="badge badge-secondary">No more records</span></h5>
      </template>
      <template slot="no-results">
        <h5><span class="badge badge-secondary">No results</span></h5>
      </template>
      <template slot="error" slot-scope="{ trigger }">
        <h5><span class="badge badge-danger">Whoops. It looks like the server is unavailable.</span>
        </h5>
        <div class="text-center">
          <el-button size="mini" @click="trigger()">Retry</el-button>
        </div>
      </template>
    </infinite-loading>
  </div>
</template>

<script>
import Item from './item';
import {addQueryParams} from "../../libs/url";

export default {
  props: {
    url: String,
  },

  data() {
    return {
      conversations: [],
      cancelToken: null,
      isRequestActive: false,
      current: null,
      infiniteLoadKey: null,
      limit: 30,
      currentLimit: 30,
    };
  },

  computed: {},

  watch: {
    url(newUrl) {
      this.refresh();
    },

    current(newValue, oldValue) {
      if (_.get(newValue, 'id') === _.get(oldValue, 'id')) {
        return;
      }

      this.$emit('conversation-select', newValue);
    },

    isRequestActive(newValue) {
      if (newValue) {
        this.$emit('refresh-start');
      } else {
        this.$emit('refresh-finish');
      }
    },
  },

  methods: {
    /**
     * @param {Object} $state
     * @return {undefined}
     */
    loadConversations($state) {
      this.currentLimit = this.currentLimit + this.limit;
      this.performLoading($state);
    },

    /**
     * @param {Object|null} $state
     * @return {undefined}
     */
    performLoading($state = null) {
      if (this.isRequestActive) {
        this.cancelToken.cancel();
      } else {
        this.isRequestActive = true;
      }

      this.cancelToken = axios.CancelToken.source();

      axios.get(addQueryParams(this.url, {limit: this.currentLimit}), {
        cancelToken: this.cancelToken.token
      })
          .then((res) => this.onLoadSuccess(res, $state))
          .catch(error => this.onLoadError(error, $state))
          .then((isCancelled) => {
            if (!isCancelled) {
              this.isRequestActive = false;
              this.refreshCurrent();
            }
          });
    },

    /**
     * @param {Object} data
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadSuccess({data: {data: result = [], stats}}, $state) {
      stats && eventBus.$emit('messages-statistics-update', stats);
      if (_.size(result) <= _.size(this.conversations) || _.size(result) < this.currentLimit) {
        $state && $state.complete();
      }

      this.conversations = result;

      _.size(result) > 0 && $state && $state.loaded();

      return false;
    },

    /**
     * @param {Object} error
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadError(error, $state) {
      if (axios.isCancel(error)) {
        return true;
      }
      $state && $state.error();

      return false;
    },

    /**
     * @param {Object} conversation
     */
    onSelect(conversation) {
      this.current = conversation;
      this.clearErrorMessageId(conversation);
    },

    /**
     * @return {undefined}
     */
    refresh() {
      this.conversations = [];
      this.currentLimit = 30;
      this.generateInfiniteLoadKey();
    },

    /**
     * @return {undefined}
     */
    silentRefresh() {
      if (this.isRequestActive) {
        return;
      }

      this.performLoading();
    },

    /**
     * @return {undefined}
     */
    generateInfiniteLoadKey() {
      this.infiniteLoadKey = this.url + moment().format('x');
    },

    /**
     * @return {undefined}
     */
    refreshCurrent() {
      const currentId = _.get(this.current, 'id', null);
      this.current = _.find(this.conversations, {id: currentId}, null);
    },

    /**
     * @param {number} brandId
     * @param {number} contactId
     * @param {Object|null} model
     */
    contactNoteStored({brandId, contactId, model}) {
      _.each(this.conversations || [], (conversation, index) => {
        if (_.get(conversation, 'contact_id') !== contactId) {
          return;
        }

        if (_.get(conversation, 'outgoing_number.brand_id') !== brandId) {
          return;
        }

        const newConversation = _.merge({}, conversation, {note: model});
        this.$set(this.conversations, index, newConversation);

        if (this.isCurrent(conversation)) {
          this.$emit('conversation-select', newConversation);
        }
      });
    },

    /**
     * @param {Number} conversationId
     * @param {Object} model
     */
    contactStored({conversationId, model}) {
      _.each(this.conversations || [], (conversation, index) => {
        if (conversationId !== _.get(conversation, 'id')) {
          return;
        }

        const newConversation = _.merge({}, conversation, {contact: model});
        this.$set(this.conversations, index, newConversation);

        if (this.isCurrent(conversation)) {
          this.$emit('conversation-select', newConversation);
        }
      });
    },

    /**
     * @param {Number} conversationId
     * @param {Object|null} list
     */
    scheduleListChanged({conversationId, list = []}) {
      _.each(this.conversations || [], (conversation, index) => {
        if (_.get(conversation, 'id') !== conversationId) {
          return;
        }

        conversation.pending_messages = list;
        this.$set(this.conversations, index, conversation);

        if (this.isCurrent(conversation)) {
          this.$emit('conversation-select', conversation);
        }
      });
    },

    /**
     * @param {Object} conversation
     */
    clearErrorMessageId(conversation) {
      const hasError = _.get(conversation, 'last_outgoing_message_error_id', null);
      if (_.isNull(hasError)) {
        return;
      }

      const url = _.get(conversation, 'conversationsClearLastOutgoingErrorUrl');
      if (_.isNull(url)) {
        return;
      }

      axios.post(url)
        .then(({data}) => {
          let item = _.get(data, 'data', {});
          _.each(this.conversations || [], (conversation, index) => {
            if (_.get(conversation, 'id') !== _.get(item, 'id')) {
              return;
            }

            this.$set(this.conversations, index, item);
          });
        });
    },

    /**
     * @param {Object} conversation
     * @return {boolean}
     */
    isCurrent(conversation) {
      return _.get(this.current, 'id') === _.get(conversation, 'id');
    },
  },

  components: {
    Item,
  }
}
</script>
